/**
 * Sharing — the one definition of what a portfolio can say about an inventor.
 *
 * Story #5648 build v2. The rule (Rob, 6 Sep 2026): the ROLE a person is
 * acting as decides which zoom levels they get; what FILLS a level is what
 * they own plus what has been SHARED with them in that role. An aggregator
 * invites an inventor to its portfolio, the inventor accepts, and only then
 * does the inventor share project by project. Accepting grants nothing by
 * itself — being in a portfolio never shows the portfolio any work.
 *
 * So every line this module produces is about SHARING, never about paying:
 * "★ by invitation", "Joined by sharing", "3 of 5 inventions", "invited
 * 12 Mar". How the relationship began is read through
 * lib/portfolioMembership.cameInByInvitation, which owns the stored field
 * value so no screen file has to carry it.
 *
 * Pure: no React, no fetching. Every figure that shows a sharing line (S6's
 * table, S5's "Shared with this portfolio" cards, C1's header) reads it from
 * here, so the three can never drift into three accounts of one relationship.
 */
import { cameInByInvitation } from '../../lib/portfolioMembership'
import type { AggregatorEntity } from '../../types'
import type { EntityGroup } from './entityGroups'

/**
 * The fields aggregator/handler.get_entities returns that the shared
 * AggregatorEntity type does not yet declare. Declared here rather than in
 * types/index.ts so this lane never edits a file three other lanes are
 * editing in the same build.
 */
export interface EntitySharingFields {
  /** How many inventions this inventor owns in total (aggregator scope only).
   * null when the count could not be read — print the shorter line, never a
   * denominator we do not have. */
  owned_project_count?: number | null
  /** When the portfolio invited them. Older rows carry only sponsor_started_at. */
  invited_at?: string
  /** When they last asked these owners to share more (7-day limit). */
  last_ask_to_share_at?: string
}

export type SharingEntity = AggregatorEntity & EntitySharingFields

/** The three ways an inventor can stand in relation to a portfolio. */
export type SharingState =
  | 'by-invitation'          // invited, accepted, and sharing what they chose
  | 'invited-not-accepted'   // the invitation is out; nothing is shared
  | 'joined-by-sharing'      // they granted this portfolio directly

export interface EntitySharing {
  state: SharingState
  /** A ★ row: this inventor is in the portfolio by invitation. */
  byInvitation: boolean
  /** Inventions this inventor has shared with this portfolio. */
  grantedCount: number
  /** Inventions this inventor owns in total, when the portfolio may know it. */
  ownedCount: number | null
  invitedAt?: string
  acceptedAt?: string
  extendedVisibility: boolean
}

/** "12 Mar" — the short form the figures print beside "invited". */
export function shortDay(value: string | undefined): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

/** "12 Mar 2026" — the long form the cards and the drawer header print. */
export function longDay(value: string | undefined): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Read one entity row's standing with this portfolio. */
export function entitySharing(entity: SharingEntity, grantedCount: number): EntitySharing {
  const byInvitation = cameInByInvitation(entity)
  const notAccepted = entity.status === 'pending'
  const state: SharingState = notAccepted
    ? 'invited-not-accepted'
    : byInvitation
      ? 'by-invitation'
      : 'joined-by-sharing'
  return {
    state,
    byInvitation,
    grantedCount,
    ownedCount: typeof entity.owned_project_count === 'number' ? entity.owned_project_count : null,
    invitedAt: entity.invited_at ?? entity.sponsor_started_at,
    acceptedAt: entity.accepted_at,
    extendedVisibility: !!entity.extended_visibility,
  }
}

/**
 * A group's standing, for S6's one-row-per-entity table. A group is by
 * invitation when any of its owners came in that way; it is still awaiting
 * acceptance only when EVERY owner is, because one accepted owner already
 * makes the group part of the portfolio.
 */
export function groupSharing(group: EntityGroup): EntitySharing {
  const members = group.members as SharingEntity[]
  const granted = group.projects.length
  if (members.length === 0) {
    return {
      state: 'joined-by-sharing',
      byInvitation: false,
      grantedCount: granted,
      ownedCount: null,
      extendedVisibility: false,
    }
  }
  const perMember = members.map((m) => entitySharing(m, granted))
  const accepted = perMember.filter((s) => s.state !== 'invited-not-accepted')
  const live = accepted.length > 0 ? accepted : perMember
  const byInvitation = live.some((s) => s.byInvitation)
  // Only sum owned counts we actually have; one unreadable count must not
  // turn a real total into a smaller wrong one.
  const owned = live.map((s) => s.ownedCount)
  const ownedCount = owned.every((n) => n !== null)
    ? owned.reduce<number>((sum, n) => sum + (n as number), 0)
    : null
  const invitedAt = live
    .map((s) => s.invitedAt)
    .filter((d): d is string => !!d)
    .sort()[0]
  const acceptedAt = live
    .map((s) => s.acceptedAt)
    .filter((d): d is string => !!d)
    .sort()[0]
  return {
    state: accepted.length === 0 ? 'invited-not-accepted' : byInvitation ? 'by-invitation' : 'joined-by-sharing',
    byInvitation,
    grantedCount: granted,
    ownedCount,
    invitedAt,
    acceptedAt,
    extendedVisibility: live.some((s) => s.extendedVisibility),
  }
}

export interface SharingLines {
  /** The first line of the Sharing cell. */
  primary: string
  /** The muted second line — the invitation date, when there is one. */
  secondary?: string
}

/** The Sharing cell exactly as figure S6 prints it. */
export function sharingLines(sharing: EntitySharing): SharingLines {
  if (sharing.state === 'invited-not-accepted') {
    return { primary: 'Invited · not yet accepted' }
  }
  if (sharing.state === 'joined-by-sharing') {
    return { primary: 'Joined by sharing' }
  }
  const invited = shortDay(sharing.invitedAt)
  const primary =
    sharing.ownedCount !== null
      ? `${sharing.grantedCount} of ${sharing.ownedCount} inventions`
      : `${sharing.grantedCount} invention${sharing.grantedCount === 1 ? '' : 's'} shared`
  return invited ? { primary, secondary: `invited ${invited}` } : { primary }
}

/**
 * "Shares with you: 3 of her 5 inventions · 1 folder hidden · extended
 * visibility on" — the S5 card's second line and C1's header line.
 * `hiddenFolderCount` is passed only when the caller really has it.
 */
export function sharesWithYouLine(
  sharing: EntitySharing,
  hiddenFolderCount?: number,
): string {
  const parts: string[] = [
    sharing.ownedCount !== null
      ? `${sharing.grantedCount} of their ${sharing.ownedCount} inventions`
      : `${sharing.grantedCount} invention${sharing.grantedCount === 1 ? '' : 's'}`,
  ]
  if (hiddenFolderCount && hiddenFolderCount > 0) {
    parts.push(`${hiddenFolderCount} folder${hiddenFolderCount === 1 ? '' : 's'} hidden`)
  }
  parts.push(`extended visibility ${sharing.extendedVisibility ? 'on' : 'off'}`)
  return parts.join(' · ')
}

/** "Invited by Meridian Ventures 12 Mar 2026 · accepted 14 Mar 2026" */
export function invitedAcceptedLine(sharing: EntitySharing, portfolioName: string): string {
  const invited = longDay(sharing.invitedAt)
  const accepted = longDay(sharing.acceptedAt)
  const who = portfolioName || 'this portfolio'
  if (!invited && !accepted) return `Invited by ${who}`
  const head = invited ? `Invited by ${who} ${invited}` : `Invited by ${who}`
  return accepted ? `${head} · accepted ${accepted}` : `${head} · not yet accepted`
}

/** The legend under S6's table, and the ★ pill's own tooltip. */
export const BY_INVITATION_LEGEND =
  '★ by invitation — an inventor never shows you more than they granted'

export function byInvitationTitle(portfolioName: string): string {
  return `★ by invitation — shares inventions with this portfolio, by invitation from ${portfolioName || 'this portfolio'}`
}

/** The line every sharing surface closes with. */
export const SHARING_IS_THEIRS_TO_GIVE =
  'Being in your portfolio never shows you their work; sharing is theirs to give.'

/** What accepting an invitation does, and does not, do. */
export function acceptingGrantsNothingLine(): string {
  return (
    'Accepting an invitation makes an inventor part of this portfolio. It grants nothing by ' +
    'itself: what you can see is decided by the inventor, project by project, under What you can see.'
  )
}

/** Extended visibility, in the words every figure uses for it. */
export const EXTENDED_VISIBILITY_RULE =
  'your switch alone; a portfolio can turn it off, never on'
